import { withAuthenticationRequired } from "@auth0/auth0-react";
import { useMemo } from "react";
import StatusBlock, { StatusBlockDisplay } from "components/StatusBlock";
import { framedByAnotherSite } from "util/framing";
import "./index.css";

function PageLoader() {
  return (
    <div className="page--loader">
      <StatusBlock
        title="Redirecting..."
        message="You are not authorized to access this page."
        display={StatusBlockDisplay.STANDALONE}
      />
    </div>
  );
}

/**
 * A signed-in page inside a frame on another website: shown nowhere but
 * in its own tab, so no other site can show it (and trick clicks on it).
 * Public pages (booking pages, form links) can still be framed.
 */
function FramedNotice() {
  return (
    <div className="page-layout">
      <div className="page--loader">
        <div className="max-w-md px-4 text-center">
          <h1 className="text-xl font-semibold text-gray-900">
            Open Tadpole in its own tab
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            To keep your account safe, Tadpole's signed-in pages don't open
            inside other websites.
          </p>
          <a
            href={window.location.href}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-5 inline-block rounded-lg bg-[rgb(var(--green-rgb))] px-4 py-2 text-sm font-semibold text-white hover:brightness-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--green-rgb))] focus-visible:ring-offset-2"
          >
            Open Tadpole
          </a>
        </div>
      </div>
    </div>
  );
}

export const ProtectedRoute = ({
  component,
  componentProps,
}: {
  component: React.ComponentType<any>;
  componentProps?: any;
}) => {
  // Wrapped once per page: a new wrapper on each render would remount the
  // page, and reload its data, whenever anything above it renders.
  const Component = useMemo(
    () =>
      withAuthenticationRequired(component, {
        onRedirecting: () => (
          <div className="page-layout">
            <PageLoader />
          </div>
        ),
      }),
    [component]
  );

  // Before anything signs in or loads: a framed signed-in page shows nothing.
  if (framedByAnotherSite()) return <FramedNotice />;

  return <Component {...componentProps} />;
};
